import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Loader2, Check,
  PhoneCall, FileText, CalendarCheck2, Store, ShoppingCart, HelpCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WANT_OPTIONS = [
  { label: "Phone Calls", icon: PhoneCall },
  { label: "Form Leads", icon: FileText },
  { label: "Appointments", icon: CalendarCheck2 },
  { label: "Store Visits", icon: Store },
  { label: "Online Sales", icon: ShoppingCart },
  { label: "Not Sure", icon: HelpCircle },
];

const CURRENT_OPTIONS = [
  "Google Ads", "Facebook / Instagram Ads", "Referrals", "SEO", "Social Media",
  "Yelp", "Angi / Thumbtack / Lead Sites", "Other", "We're not currently advertising",
];
const EXCLUSIVE = ["We're not currently advertising"];

const AD_SPEND = [
  "$0", "Under $1,000/month", "$1,000–$2,500/month", "$2,500–$5,000/month",
  "$5,000–$10,000/month", "$10,000+/month", "Not sure",
];

const STEP_TITLES = [
  "What Kind Of Business Do You Own?", "What Do You Want More Of?",
  "What Is A New Customer Worth?", "How Are You Getting Customers Now?",
  "Current Advertising Spend", "Where Should We Send Your Audit?",
];

const NEXT_STEPS = [
  "We Review Your Business",
  "We Identify The Opportunities",
  "We Walk You Through What We Found",
  "You Decide What Happens Next",
];

const INPUT =
  "h-14 w-full rounded-xl border border-[#272732] bg-[#0B0B10] px-5 text-base text-white placeholder:text-zinc-600 transition-colors focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const Field = ({ label, optional, children }) => (
  <label className="block">
    <span className="mb-2 block text-sm font-bold text-zinc-300">
      {label}
      {optional && <span className="ml-2 text-xs font-medium text-zinc-500">(optional)</span>}
    </span>
    {children}
  </label>
);

const initialData = {
  business_name: "", website: "", industry: "", city: "",
  wants: [], customer_value: "", current_marketing: [], ad_spend: "",
  name: "", email: "", phone: "",
};

export default function AuditPage() {
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [data, setData] = useState(initialData);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setData((d) => ({ ...d, [k]: e.target.value }));

  const toggleList = (key, value, exclusive = []) =>
    setData((d) => {
      let list = d[key];
      if (list.includes(value)) list = list.filter((v) => v !== value);
      else if (exclusive.includes(value)) list = [value];
      else list = [...list.filter((v) => !exclusive.includes(v)), value];
      return { ...d, [key]: list };
    });

  const validate = () => {
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email);
    if (step === 0 && !data.business_name.trim()) return "Tell us your business name.";
    if (step === 1 && data.wants.length === 0) return "Pick at least one — 'Not Sure' is a perfectly good answer.";
    if (step === 3 && data.current_marketing.length === 0)
      return "Select at least one — including 'We're not currently advertising'.";
    if (step === 4 && !data.ad_spend) return "Choose a range. Every answer is welcome here.";
    if (step === 5) {
      if (!data.name.trim() || !data.phone.trim()) return "We need your name and phone number.";
      if (!emailOk) return "Please enter a valid email address.";
    }
    return "";
  };

  const next = () => {
    const msg = validate();
    if (msg) return setError(msg);
    setError("");
    setDir(1);
    setStep((s) => Math.min(s + 1, 5));
  };

  const back = () => {
    setError("");
    setDir(-1);
    setStep((s) => Math.max(s - 1, 0));
  };

  const submit = async () => {
    const msg = validate();
    if (msg) return setError(msg);
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/audit`, data);
      setDone(true);
      window.scrollTo(0, 0);
    } catch (e) {
      setError("Something went wrong sending your audit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const stepBody = [
    <div key="s0" className="space-y-5">
      <Field label="Business Name">
        <input data-testid="audit-input-business-name" className={INPUT} placeholder="e.g. Desert Peak Plumbing" value={data.business_name} onChange={set("business_name")} />
      </Field>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Website" optional>
          <input data-testid="audit-input-website" className={INPUT} placeholder="yourwebsite.com" value={data.website} onChange={set("website")} />
        </Field>
        <Field label="Industry" optional>
          <input data-testid="audit-input-industry" className={INPUT} placeholder="e.g. HVAC, dental, law" value={data.industry} onChange={set("industry")} />
        </Field>
      </div>
      <Field label="City / Service Area" optional>
        <input data-testid="audit-input-city" className={INPUT} placeholder="e.g. Las Vegas valley, or nationwide" value={data.city} onChange={set("city")} />
      </Field>
    </div>,
    <div key="s1">
      <p className="mb-5 text-sm font-bold text-zinc-300">What do you want more of? <span className="font-medium text-zinc-500">(Pick all that apply)</span></p>
      <div className="grid grid-cols-2 gap-3">
        {WANT_OPTIONS.map((w) => {
          const active = data.wants.includes(w.label);
          return (
            <button
              key={w.label}
              type="button"
              data-testid={`audit-want-${slug(w.label)}`}
              onClick={() => toggleList("wants", w.label)}
              className={`flex min-h-[80px] items-center gap-3 rounded-xl border px-4 py-3 text-left text-sm font-bold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
                active
                  ? "border-purple-500 bg-purple-600/15 text-white"
                  : "border-white/10 bg-white/[0.03] text-zinc-300 hover:border-purple-500/40"
              }`}
            >
              <w.icon className={`h-5 w-5 shrink-0 ${active ? "text-purple-400" : "text-zinc-500"}`} />
              {w.label}
            </button>
          );
        })}
      </div>
    </div>,
    <div key="s2">
      <Field label="Approximately how much revenue is an average new customer worth?" optional>
        <div className="flex items-center rounded-xl border border-[#272732] bg-[#0B0B10] px-5 focus-within:border-purple-500">
          <span className="font-display text-xl font-extrabold text-purple-400">$</span>
          <input
            data-testid="audit-input-customer-value"
            inputMode="numeric"
            className="h-14 w-full bg-transparent px-3 text-base text-white placeholder:text-zinc-600 focus:outline-none"
            placeholder="e.g. 500 — a rough guess is fine"
            value={data.customer_value}
            onChange={set("customer_value")}
          />
        </div>
      </Field>
      <p className="mt-4 text-xs leading-relaxed text-zinc-500">
        This helps us understand what a lead is actually worth to your business.
      </p>
    </div>,
    <div key="s3">
      <p className="mb-4 text-sm font-bold text-zinc-300">How are you getting customers now? <span className="font-medium text-zinc-500">(Select all)</span></p>
      <div className="flex flex-wrap gap-2.5">
        {CURRENT_OPTIONS.map((c) => {
          const active = data.current_marketing.includes(c);
          return (
            <button
              key={c}
              type="button"
              data-testid={`audit-current-${slug(c)}`}
              onClick={() => toggleList("current_marketing", c, EXCLUSIVE)}
              className={`rounded-full border px-4 py-2.5 text-sm font-bold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
                active
                  ? "border-purple-500 bg-purple-600 text-white"
                  : "border-white/10 bg-white/5 text-zinc-300 hover:border-purple-500/40"
              }`}
            >
              {c}
            </button>
          );
        })}
      </div>
    </div>,
    <div key="s4">
      <p className="mb-5 text-sm font-bold text-zinc-300">What are you currently spending on advertising?</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {AD_SPEND.map((b, i) => (
          <button
            key={b}
            type="button"
            data-testid={`audit-spend-${i}`}
            onClick={() => setData((d) => ({ ...d, ad_spend: b }))}
            className={`flex items-center justify-between rounded-xl border px-5 py-4 text-left text-sm font-bold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 ${
              data.ad_spend === b
                ? "border-purple-500 bg-purple-600/15 text-white"
                : "border-white/10 bg-white/[0.03] text-zinc-300 hover:border-purple-500/40"
            }`}
          >
            {b}
            {data.ad_spend === b && <Check className="h-4 w-4 text-purple-400" />}
          </button>
        ))}
      </div>
      <p className="mt-4 text-xs text-zinc-500">
        There's no wrong answer — we work with businesses at every stage.
      </p>
    </div>,
    <div key="s5" className="space-y-5">
      <Field label="Name">
        <input data-testid="audit-input-name" className={INPUT} placeholder="Your name" value={data.name} onChange={set("name")} />
      </Field>
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field label="Phone">
          <input data-testid="audit-input-phone" type="tel" className={INPUT} placeholder="(702) 555-0100" value={data.phone} onChange={set("phone")} />
        </Field>
        <Field label="Email">
          <input data-testid="audit-input-email" type="email" className={INPUT} placeholder="you@business.com" value={data.email} onChange={set("email")} />
        </Field>
      </div>
    </div>,
  ];

  return (
    <div data-testid="audit-page" className="min-h-screen bg-[#05050A] text-white">
      <div className="vp-noise pointer-events-none fixed inset-0 z-[70] opacity-[0.03]" />
      <header className="border-b border-white/10">
        <div className="mx-auto flex h-[76px] max-w-4xl items-center justify-between px-5 sm:px-8">
          <Link to="/" data-testid="audit-logo-link" aria-label="Viral Pulse Agency — home">
            <img src="/assets/vp-wordmark.png" alt="Viral Pulse Agency" className="h-9 w-auto sm:h-10" />
          </Link>
          <Link
            to="/"
            data-testid="audit-back-link"
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-400 transition-colors hover:text-purple-400"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back To Site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-12 sm:px-8 sm:py-16">
        <p className="text-center text-xs font-bold uppercase tracking-[0.28em] text-purple-400">
          Free Lead Generation Audit
        </p>
        <h1 className="mt-4 text-center font-display text-3xl font-extrabold leading-[0.95] tracking-tighter text-white sm:text-4xl lg:text-5xl">
          Your Next Customer Is Out There.{" "}
          <span className="text-purple-500">Let's Go Get Them.</span>
        </h1>
        <p className="mx-auto mt-5 max-w-xl text-center text-sm leading-relaxed text-zinc-400 sm:text-base">
          No obligation. We look at your actual business and show you what we'd do.
        </p>

        <div
          data-testid="audit-form"
          className="mt-10 rounded-3xl border border-[#272732] bg-[#121217] p-6 shadow-2xl shadow-black/50 sm:p-10"
        >
          <AnimatePresence mode="wait">
            {done ? (
              <motion.div
                key="success"
                data-testid="audit-success"
                initial={{ opacity: 0, scale: 0.97 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="py-6"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-purple-600">
                  <CheckCircle2 className="h-8 w-8 text-white" />
                </span>
                <h2 className="mt-8 font-display text-4xl font-extrabold tracking-tighter text-white sm:text-5xl">
                  We've Got It.
                </h2>
                <p className="mt-5 max-w-xl text-base leading-relaxed text-zinc-400">
                  Our team will take a look at your business, your digital presence
                  and the opportunities we see for growth.
                </p>
                <div className="mt-9 rounded-2xl border border-white/10 bg-white/[0.03] p-6 text-left sm:p-8">
                  <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-purple-400">
                    What Happens Next
                  </p>
                  <ol className="mt-5 space-y-4">
                    {NEXT_STEPS.map((s, i) => (
                      <li key={s} className="flex items-center gap-4">
                        <span className="font-display text-2xl font-extrabold text-purple-500/60">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span className="font-display text-lg font-extrabold tracking-tight text-white">
                          {s}
                        </span>
                      </li>
                    ))}
                  </ol>
                </div>
                <p className="mt-7 max-w-xl text-sm leading-relaxed text-zinc-500">
                  No pressure. If you want Viral Pulse to execute the strategy, we'll
                  show you how we can help. If not, you'll still walk away knowing
                  more about your marketing.
                </p>
                <Link
                  to="/"
                  data-testid="audit-success-home-link"
                  className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-purple-400 transition-colors hover:text-purple-300"
                >
                  <ArrowLeft className="h-4 w-4" /> Back to the homepage
                </Link>
              </motion.div>
            ) : (
              <motion.div key="form" exit={{ opacity: 0, y: -16 }} transition={{ duration: 0.3 }}>
                <div className="mb-8">
                  <div className="flex items-center justify-between gap-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.25em] text-purple-400">
                      Step {step + 1} of 6
                    </p>
                    <p className="text-right font-display text-sm font-extrabold text-white sm:text-base">
                      {STEP_TITLES[step]}
                    </p>
                  </div>
                  <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white/10">
                    <motion.div
                      data-testid="audit-progress-bar"
                      className="h-full rounded-full bg-purple-500"
                      animate={{ width: `${((step + 1) / 6) * 100}%` }}
                      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    />
                  </div>
                </div>

                <AnimatePresence mode="wait" custom={dir}>
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 44 * dir }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -44 * dir }}
                    transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  >
                    {stepBody[step]}
                  </motion.div>
                </AnimatePresence>

                {error && (
                  <p data-testid="audit-form-error" className="mt-5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm font-medium text-red-300">
                    {error}
                  </p>
                )}

                <div className="mt-8 flex items-center justify-between gap-4">
                  {step > 0 ? (
                    <button
                      type="button"
                      data-testid="audit-back-button"
                      onClick={back}
                      className="flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-6 py-3.5 text-sm font-bold text-zinc-300 transition-colors hover:bg-white/10"
                    >
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                  ) : (
                    <span />
                  )}
                  {step < 5 ? (
                    <button
                      type="button"
                      data-testid="audit-next-button"
                      onClick={next}
                      className="group flex items-center gap-2 rounded-full bg-purple-600 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-purple-500"
                    >
                      Next
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid="audit-submit-button"
                      onClick={submit}
                      disabled={submitting}
                      className="flex items-center gap-2 rounded-full bg-purple-600 px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-purple-500 disabled:opacity-60"
                    >
                      {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                      SHOW ME HOW TO GET MORE CUSTOMERS
                    </button>
                  )}
                </div>
                {step === 5 && (
                  <p className="mt-4 text-xs leading-relaxed text-zinc-500">
                    No obligation. We look at your actual business and show you
                    what we'd do.
                  </p>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
